/**
 * WbInput / WbTextarea — invoerveld op de set.
 *
 * 2 px inktrand, 52 px hoog, label klein bovenop (zelfde vak als WbFactRow
 * zodat een feit en een invoer naast elkaar kunnen staan). `mono` voor
 * getallen en nummers. Foutregel eronder in geld-rood (het is een fout, geen
 * geld; de kleur is de enige rode van de set).
 */

import { type InputHTMLAttributes, type TextareaHTMLAttributes, forwardRef } from 'react';
import clsx from 'clsx';

interface Gedeeld {
  label?: string;
  fout?: string | null;
  mono?: boolean;
  /** Tekst rechts in het vak (cm, m², €). */
  eenheid?: string;
  wrapperClassName?: string;
}

export interface WbInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'>, Gedeeld {
  className?: string;
}

export const WbInput = forwardRef<HTMLInputElement, WbInputProps>(
  ({ label, fout, mono = false, eenheid, wrapperClassName, className, ...props }, ref) => (
    <label className={clsx('block', wrapperClassName)}>
      <span className={clsx('min-h-tap bg-wb-vlak border-wb rounded-wb px-3 py-1.5 flex flex-col justify-center', fout ? 'border-wb-geldink' : 'border-wb-rand')}>
        {label && <span className="block text-[11px] font-bold uppercase tracking-wide text-wb-inkt2 leading-tight">{label}</span>}
        <span className="flex items-center gap-2">
          <input
            ref={ref}
            className={clsx(
              'wb-focus w-full bg-transparent text-wb-inkt placeholder:text-wb-inkt3 outline-none',
              mono ? 'font-mono text-xl font-semibold' : 'text-base font-semibold',
              className
            )}
            {...props}
          />
          {eenheid && <span className="font-mono text-sm text-wb-inkt2 shrink-0">{eenheid}</span>}
        </span>
      </span>
      {fout && <span className="block text-sm font-semibold text-wb-geldink mt-1">{fout}</span>}
    </label>
  )
);
WbInput.displayName = 'WbInput';

export interface WbTextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'>, Omit<Gedeeld, 'mono' | 'eenheid'> {
  className?: string;
}

export const WbTextarea = forwardRef<HTMLTextAreaElement, WbTextareaProps>(
  ({ label, fout, wrapperClassName, className, rows = 3, ...props }, ref) => (
    <label className={clsx('block', wrapperClassName)}>
      {label && <span className="block text-[11px] font-bold uppercase tracking-wide text-wb-inkt2 mb-1">{label}</span>}
      <textarea
        ref={ref}
        rows={rows}
        className={clsx(
          'wb-focus w-full bg-wb-vlak border-wb rounded-wb px-3 py-2 text-base text-wb-inkt placeholder:text-wb-inkt3 resize-none',
          fout ? 'border-wb-geldink' : 'border-wb-rand',
          className
        )}
        {...props}
      />
      {fout && <span className="block text-sm font-semibold text-wb-geldink mt-1">{fout}</span>}
    </label>
  )
);
WbTextarea.displayName = 'WbTextarea';

/** Select op de set; zelfde vak als WbInput. */
export interface WbSelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'className'>, Pick<Gedeeld, 'label' | 'fout' | 'wrapperClassName'> {
  className?: string;
}

export function WbSelect({ label, fout, wrapperClassName, className, children, ...props }: WbSelectProps) {
  return (
    <label className={clsx('block', wrapperClassName)}>
      {label && <span className="block text-[11px] font-bold uppercase tracking-wide text-wb-inkt2 mb-1">{label}</span>}
      <select
        className={clsx(
          'wb-focus w-full min-h-tap bg-wb-vlak border-wb rounded-wb px-3 text-base font-semibold text-wb-inkt',
          fout ? 'border-wb-geldink' : 'border-wb-rand',
          className
        )}
        {...props}
      >
        {children}
      </select>
      {fout && <span className="block text-sm font-semibold text-wb-geldink mt-1">{fout}</span>}
    </label>
  );
}
